import type { StateCreator } from "zustand";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import { clearUrlParamsState } from "@/store/url-params/urlParamsCodec";
import type {
  FilterSelectionActions,
  NewsFeedParamsActions,
  NewsFeedParamsSlice,
  NewsFeedPeriod,
  SelectedFilterType,
} from "./newsFeed-params.slice";

export const createNewsFeedParamsActions: StateCreator<
  NewsFeedParamsSlice,
  [["zustand/devtools", never]],
  [],
  NewsFeedParamsActions & FilterSelectionActions
> = (set, get) => ({
  /**
   * Sets the period for data aggregation (day / week / month only).
   */
  setPeriod: (period: NewsFeedPeriod) => {
    useFeedContentManagementStore.getState().resetFeedVolumeView();
    set({ period }, false, "[User] News Feed Params: Set period");
  },

  /**
   * Sets the number of filter days (dates will be derived automatically)
   */
  setFilterDays: (days: number) => {
    useFeedContentManagementStore.getState().resetFeedVolumeView();
    set(
      {
        filterDays: days,
        isCustomDaysRange: false,
        startDate: undefined,
        endDate: undefined,
      },
      false,
      "[User] News Feed Params: Set filter days",
    );
  },

  /**
   * Sets custom start and end dates, enables custom range mode
   */
  setCustomDateRange: (startDate: Date, endDate: Date) => {
    useFeedContentManagementStore.getState().resetFeedVolumeView();
    set(
      {
        startDate,
        endDate,
        isCustomDaysRange: true,
        filterDays: undefined,
      },
      false,
      "[User] News Feed Params: Set custom date range",
    );
  },

  /**
   * Toggles selection of a filter item for a specific category
   */
  toggleFilterSelection: (
    selection: ShareVoiceSidebarCategory,
    item: string | number,
  ) => {
    const currentFilters = get().selectedFilters;
    const itemStr = String(item); // Convert to string for consistency

    // Create a new Map
    const newFilters = new Map(currentFilters);

    // Check if category exists in the Map
    const currentItems = newFilters.get(selection);
    if (currentItems) {
      if (currentItems.includes(itemStr)) {
        // Item exists, remove it
        const updatedItems = currentItems.filter(
          (existingItem) => existingItem !== itemStr,
        );

        // If no items left, remove the category entirely
        if (updatedItems.length === 0) {
          newFilters.delete(selection);
        } else {
          newFilters.set(selection, updatedItems);
        }
      } else {
        // Item doesn't exist, add it
        newFilters.set(selection, [...currentItems, itemStr]);
      }
    } else {
      // Category doesn't exist, create it with the item
      newFilters.set(selection, [itemStr]);
    }

    set(
      { selectedFilters: newFilters },
      false,
      "[User] News Feed Params: Toggle filter selection",
    );
  },

  /**
   * Clears all selected filters
   */
  clearAllSelectedFilters: () => {
    set(
      { selectedFilters: new Map() as SelectedFilterType },
      false,
      "[User] News Feed Params: Clear all selected filters",
    );
  },

  /**
   * Clears selected filters for a specific category
   */
  clearSelectedFiltersByCategory: (category: ShareVoiceSidebarCategory) => {
    const currentFilters = get().selectedFilters;
    const newFilters = new Map(currentFilters);

    newFilters.delete(category);

    set(
      { selectedFilters: newFilters },
      false,
      `[User] News Feed Params: Clear selected filters for category ${category}`,
    );
  },

  /**
   * Clears all selected dynamic filters (filters that exist in dynamicFilters map)
   */
  clearDynamicFilters: () => {
    const currentFilters = get().selectedFilters;
    const { dynamicFilters } = useNewsFeedFilters.getState();
    const newFilters = new Map<ShareVoiceSidebarCategory, string[]>();

    // Only keep categories that are NOT in dynamicFilters
    for (const [category, selectedItems] of currentFilters) {
      if (!dynamicFilters.has(category)) {
        newFilters.set(category, selectedItems);
      }
    }

    set(
      { selectedFilters: newFilters },
      false,
      "[System] News Feed Params: Clear dynamic filters",
    );
  },

  /**
   * Clears all state and resets to defaults
   */
  clearState: () => {
    set(
      {
        ...clearUrlParamsState(),
      },
      false,
      "[System] News Feed Params: Clear state",
    );
  },

  /**
   * Removes specific filter items from a category
   * If all items are removed, the category is removed from selectedFilters
   */
  removeFilterItemsByCategory: (
    category: ShareVoiceSidebarCategory,
    itemsToRemove: string[],
  ) => {
    const currentFilters = get().selectedFilters;
    const currentItems = currentFilters.get(category);

    if (!currentItems || currentItems.length === 0) {
      return;
    }

    const itemsToRemoveSet = new Set(itemsToRemove);
    const updatedItems = currentItems.filter(
      (item) => !itemsToRemoveSet.has(item),
    );

    const newFilters = new Map(currentFilters);
    if (updatedItems.length === 0) {
      newFilters.delete(category);
    } else {
      newFilters.set(category, updatedItems);
    }

    set(
      { selectedFilters: newFilters },
      false,
      `[User] News Feed Params: Remove filter items from category ${category}`,
    );
  },
});
